/**
 * feat/frontend-import-ui — Página "Importar datos" (self-service de carga masiva).
 *
 * Envuelve los endpoints existentes `/imports/*`:
 *   - GET  /imports/template   → descarga la plantilla .xlsx (Instrucciones/Productos/Ventas)
 *   - POST /imports/products   → multipart (campo `file`); dry-run por defecto, `?dryRun=false` = commit
 *   - POST /imports/sales      → idem + `sourceModule` (deriva del módulo de comercio del tenant)
 *   - GET  /imports/batches    → historial/auditoría (máx 50, orden desc)
 *
 * Principios (Plan §9): plantilla-Excel-céntrico · dry-run OBLIGATORIO (preview + errores
 * antes de commit) · audit visible (historial) · skip de filas malas con motivo legible.
 *
 * Gating: @RequiresModule('gastro','boutique','gym') + product_catalog:write. El Cajero
 * (sin ese permiso) y los tenants sin módulo de comercio NO ven ni acceden esta página
 * (oculta en el sidebar + guard de render aquí; el API además responde 403).
 *
 * ── Ventas + Gap #3 (warnings) ──────────────────────────────────────────────────────
 * El importador de ventas, ante una línea con precio cuyo producto NO existe en el
 * catálogo, la carga como línea "suelta" (productId:null). Útil para cargos legítimos
 * (envío/propina), peligroso si es un SKU con typo. El backend ahora la reporta como
 * WARNING con identidad de línea en el DRY-RUN (`ImportResult.warnings`). Esta UI exige
 * ACKNOWLEDGMENT de esos warnings antes de habilitar "Confirmar carga" → el usuario
 * revisa, corrige typos, re-corre el dry-run y commitea limpio. (El gate es el dry-run
 * porque, por idempotencia (tenant, external_id), un typo commiteado no se corrige
 * re-importando el Excel: el folio ya existe y se saltaría.)
 */
import { useCallback, useEffect, useState } from 'react';
import { Download, FileSpreadsheet, ShoppingCart, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { api, apiUpload, apiDownload, ApiError } from '../lib/api';
import { useAuth, type MeResponse } from '../auth/AuthProvider';
import { useActiveWriteBranch } from '../lib/useActiveWriteBranch';

const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-3.5 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'rounded-xl bg-white px-3.5 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

const COMMERCE_MODULES = ['gastro', 'boutique', 'gym'];

/** Mismo criterio que Dashboard.hasPermission: SUPER_USER bypassa; si no, mira permissions[]. */
function can(user: MeResponse | null, perm: string): boolean {
  if (!user) return false;
  if (user.role === 'SUPER_USER') return true;
  return Array.isArray(user.permissions) && user.permissions.includes(perm);
}

// ── Tipos espejo del contrato del backend (dto/import.dto.ts, import-batch.entity.ts) ──
interface ImportRowError { row: number; reason: string; }
interface ImportRowWarning { row: number; kind: string; label: string; reason: string; }
interface ImportPreviewItem { row: number; action: 'insert' | 'update' | 'skip'; summary: string; }
interface ImportResult {
  entityType: 'products' | 'sales';
  dryRun: boolean;
  batchId: string | null;
  filename: string | null;
  fileHash: string | null;
  fileAlreadyImported: boolean;
  total: number;
  inserted: number;
  updated: number;
  skipped: number;
  errors: ImportRowError[];
  warnings: ImportRowWarning[];
  preview: ImportPreviewItem[];
}
interface ImportBatch {
  id: string;
  entityType: 'products' | 'sales';
  status: 'DRY_RUN' | 'COMMITTED';
  filename: string | null;
  rowsTotal: number;
  rowsInserted: number;
  rowsUpdated: number;
  rowsSkipped: number;
  rowsError: number;
  createdByUserId: string | null;
  createdAt: string;
}

type Entity = 'products' | 'sales';

const ACTION_LABEL: Record<ImportPreviewItem['action'], string> = { insert: 'Nuevo', update: 'Actualiza', skip: 'Se omite' };
const ACTION_CLASS: Record<ImportPreviewItem['action'], string> = {
  insert: 'bg-emerald-50 text-emerald-700',
  update: 'bg-sky-50 text-sky-700',
  skip: 'bg-slate-100 text-slate-500',
};

const ENTITY_CFG: Record<Entity, { noun: string; unitSingular: string; unitPlural: string; sheet: string }> = {
  products: { noun: 'productos', unitSingular: 'fila de datos', unitPlural: 'filas de datos', sheet: 'Productos' },
  sales: { noun: 'ventas', unitSingular: 'venta', unitPlural: 'ventas', sheet: 'Ventas' },
};

function humanError(msg: unknown, fallback: string): string {
  if (msg instanceof ApiError) return msg.body?.message || msg.message || fallback;
  if (msg instanceof Error) return msg.message || fallback;
  return fallback;
}

/** Exporta errores + warnings del reporte a CSV (el historial solo guarda el CONTEO de
 *  errores, no los mensajes — Gap #2 — y no guarda warnings). Se descarga antes de
 *  navegar para no perderlos. BOM para Excel. */
function downloadIssuesCsv(errors: ImportRowError[], warnings: ImportRowWarning[]): void {
  const esc = (c: string) => `"${String(c).replace(/"/g, '""')}"`;
  const rows: string[][] = [['tipo', 'fila_de_datos', 'detalle', 'motivo']];
  for (const e of errors) rows.push(['error', String(e.row), '', e.reason]);
  for (const w of warnings) rows.push(['aviso', String(w.row), w.label, w.reason]);
  const blob = new Blob(['﻿' + rows.map((r) => r.map(esc).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'revision-importacion.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function Stat({ n, label, tone }: { n: number; label: string; tone: 'ok' | 'sky' | 'slate' | 'rose' }) {
  const cls = {
    ok: 'bg-emerald-50 text-emerald-700',
    sky: 'bg-sky-50 text-sky-700',
    slate: 'bg-slate-100 text-slate-600',
    rose: 'bg-rose-50 text-rose-700',
  }[tone];
  return (
    <div className={`rounded-xl px-4 py-3 text-center ${cls}`}>
      <p className="font-serif text-2xl tabular-nums">{n}</p>
      <p className="text-[11px] opacity-80">{label}</p>
    </div>
  );
}

// ── Panel de import reutilizable (productos y ventas: subir → dry-run auto → preview → confirmar) ──
function ImportPanel({ entity, sourceModule, writeBranchId = null, needsChoice = false, onCommitted }: { entity: Entity; sourceModule?: string; writeBranchId?: string | null; needsChoice?: boolean; onCommitted: () => void }) {
  const cfg = ENTITY_CFG[entity];
  const [file, setFile] = useState<File | null>(null);
  const [dry, setDry] = useState<ImportResult | null>(null);
  const [committed, setCommitted] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [warnAck, setWarnAck] = useState(false);

  const reset = () => { setFile(null); setDry(null); setCommitted(null); setErr(null); setReviewed(false); setWarnAck(false); };

  const pathFor = (dryRun: boolean) => {
    const qs = new URLSearchParams({ dryRun: String(dryRun) });
    if (entity === 'sales' && sourceModule) qs.set('sourceModule', sourceModule);
    // feat/branch-write-require-active — el COMMIT de PRODUCTOS escribe stock inicial
    // por sucursal: el backend exige ?branchId= si el archivo trae stock (para el dry-run
    // no aplica, no escribe). Va la sucursal activa de la sesión. (El import de VENTAS no
    // se toca; el commit se bloquea aparte cuando falta destino: needsChoice.)
    if (entity === 'products' && !dryRun && writeBranchId) qs.set('branchId', writeBranchId);
    return `/imports/${entity}?${qs.toString()}`;
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = ''; // permite re-seleccionar el mismo archivo
    if (!f) return;
    setFile(f); setDry(null); setCommitted(null); setErr(null); setReviewed(false); setWarnAck(false);
    // Dry-run automático (preview obligatorio ANTES del commit).
    setBusy(true);
    try {
      const r = await apiUpload<ImportResult>(pathFor(true), f);
      setDry(r);
    } catch (e2) {
      setErr(humanError(e2, 'No se pudo validar el archivo.'));
      setFile(null);
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!file || !dry) return;
    setBusy(true); setErr(null);
    try {
      const r = await apiUpload<ImportResult>(pathFor(false), file);
      setCommitted(r);
      onCommitted();
    } catch (e2) {
      setErr(humanError(e2, 'No se pudo confirmar la carga.'));
    } finally { setBusy(false); }
  };

  const report = committed ?? dry;
  const affected = report ? report.inserted + report.updated + report.skipped : 0;
  const hasWarnings = !!report && report.warnings.length > 0;
  // feat/branch-write-require-active — el commit de PRODUCTOS puede escribir stock inicial
  // por sucursal → sin destino único (consolidado + >1 sucursal) se bloquea el commit.
  const branchBlocked = entity === 'products' && needsChoice;
  const canConfirm = !!dry && !committed && !busy && reviewed && affected > 0 && (!hasWarnings || warnAck) && !branchBlocked;

  return (
    <div className="space-y-5">
      {/* 1. Subir archivo */}
      <div>
        <p className="mb-1.5 text-xs font-medium text-slate-600">1. Sube tu archivo de {cfg.noun} (hoja «{cfg.sheet}» de la plantilla, .xlsx)</p>
        <input
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          disabled={busy}
          onChange={onFile}
          className="w-full text-xs file:mr-3 file:rounded-lg file:border file:border-slate-200 file:bg-white file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-50 disabled:opacity-50"
        />
        {file && <p className="mt-1.5 text-[11px] text-slate-500">Archivo: <span className="font-medium text-slate-700">{file.name}</span></p>}
      </div>

      {busy && !committed && <p className="text-xs text-slate-500">Validando el archivo…</p>}
      {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}

      {report && (
        <div className="space-y-4">
          {report.fileAlreadyImported && !committed && (
            <p className="alert-warn flex items-start gap-2 rounded-xl px-3 py-2 text-xs">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Este archivo ya se importó antes (mismo contenido). Si vuelves a confirmar, las filas se detectarán como duplicadas y se omitirán (no se duplica nada).
            </p>
          )}

          {/* Conteos del dry-run / commit */}
          <div>
            <p className="mb-2 text-xs font-medium text-slate-600">
              2. {committed ? 'Resultado de la carga' : 'Vista previa (nada se ha guardado todavía)'} — {report.total} {report.total === 1 ? cfg.unitSingular : cfg.unitPlural}
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat n={report.inserted} label="Nuevos" tone="ok" />
              <Stat n={report.updated} label="Actualizados" tone="sky" />
              <Stat n={report.skipped} label="Omitidos (duplicados)" tone="slate" />
              <Stat n={report.errors.length} label="Con error" tone="rose" />
            </div>
          </div>

          {/* Warnings (Gap #3) — filas que SÍ se cargan pero requieren revisión */}
          {hasWarnings && (
            <div className={`space-y-2 rounded-xl border px-4 py-3 ${committed ? 'border-slate-200 bg-slate-50' : 'border-amber-300 bg-amber-50/70'}`}>
              <p className="flex items-start gap-2 text-xs font-medium text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                {report.warnings.length} línea{report.warnings.length === 1 ? '' : 's'} se cargará{report.warnings.length === 1 ? '' : 'n'} SIN producto asociado.
                Confirma que sean cargos legítimos (envío, propina, servicio) y no SKUs con error de dedo.
              </p>
              <div className="max-h-40 overflow-auto rounded-lg border border-amber-200 bg-white">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-amber-50 text-left text-[10px] uppercase tracking-wide text-amber-700">
                    <tr><th className="px-3 py-1.5">Fila</th><th className="px-3 py-1.5">Lo que escribiste</th><th className="px-3 py-1.5">Detalle</th></tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100">
                    {report.warnings.map((w, i) => (
                      <tr key={i}>
                        <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-amber-700">#{w.row}</td>
                        <td className="px-3 py-1.5 font-medium text-slate-700">{w.label}</td>
                        <td className="px-3 py-1.5 text-slate-600">{w.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Errores legibles (fila de datos + motivo) */}
          {report.errors.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-slate-600">
                Filas con problema — se <span className="font-semibold">omiten</span>, el resto sí se carga
              </p>
              <div className="max-h-56 overflow-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
                    <tr><th className="px-3 py-1.5">Fila de datos</th><th className="px-3 py-1.5">Qué revisar</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.errors.map((e, i) => (
                      <tr key={i}>
                        <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-slate-500">#{e.row}</td>
                        <td className="px-3 py-1.5 text-slate-700">{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-400">
                "Fila de datos" cuenta solo las filas con contenido (sin el encabezado); puede no coincidir con el número de fila de Excel.
              </p>
            </div>
          )}

          {(report.errors.length > 0 || hasWarnings) && (
            <button className={btnGhost} onClick={() => downloadIssuesCsv(report.errors, report.warnings)}>
              <span className="inline-flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> Descargar revisión (.csv)</span>
            </button>
          )}

          {/* Muestra de lo que se insertará/actualizará */}
          {report.preview.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-slate-600">
                Muestra {report.preview.length < affected ? `(${report.preview.length} de ${affected})` : ''}
              </p>
              <div className="max-h-56 overflow-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
                    <tr><th className="px-3 py-1.5">Fila</th><th className="px-3 py-1.5">Acción</th><th className="px-3 py-1.5">Detalle</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.preview.map((p, i) => (
                      <tr key={i}>
                        <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-slate-500">#{p.row}</td>
                        <td className="px-3 py-1.5">
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${ACTION_CLASS[p.action]}`}>{ACTION_LABEL[p.action]}</span>
                        </td>
                        <td className="px-3 py-1.5 text-slate-700">{p.summary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 3. Confirmar / Éxito */}
          {committed ? (
            <div className="flex flex-col gap-3 rounded-xl bg-emerald-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex items-center gap-2 text-sm text-emerald-800">
                <CheckCircle2 className="h-4 w-4" />
                Carga confirmada: {committed.inserted} nuevos{committed.updated ? `, ${committed.updated} actualizados` : ''}
                {committed.skipped ? `, ${committed.skipped} omitidos` : ''}.
              </p>
              <button className={btnPrimary} onClick={reset}>Importar otro archivo</button>
            </div>
          ) : (
            <div className="space-y-3 border-t border-slate-100 pt-4">
              {branchBlocked && (
                <p className="alert-warn flex items-start gap-2 rounded-xl px-3 py-2 text-xs">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  Estás en vista consolidada ("Todas"). Si el archivo trae stock inicial, elige una sucursal concreta en la barra superior antes de confirmar: el stock se cargará en esa sucursal.
                </p>
              )}
              {hasWarnings && (
                <label className="flex items-start gap-2 text-xs text-amber-900">
                  <input type="checkbox" checked={warnAck} onChange={(e) => setWarnAck(e.target.checked)} className="mt-0.5" disabled={busy} />
                  <span>Revisé las {report.warnings.length} línea{report.warnings.length === 1 ? '' : 's'} sin producto y confirmo que son correctas (no son SKUs con typo).</span>
                </label>
              )}
              <label className="flex items-start gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} className="mt-0.5" disabled={busy} />
                <span>Revisé la vista previa y quiero guardar estos {cfg.noun}.</span>
              </label>
              <div className="flex justify-end gap-2">
                <button className={btnGhost} onClick={reset} disabled={busy}>Descartar</button>
                <button className={btnPrimary} onClick={confirm} disabled={!canConfirm}>
                  {busy ? 'Guardando…' : 'Confirmar carga'}
                </button>
              </div>
              {affected === 0 && report.errors.length > 0 && (
                <p className="text-right text-[11px] text-slate-400">No hay {cfg.unitPlural} válidas para cargar; corrige los errores y vuelve a subir el archivo.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Historial de batches (auditoría) ──
function BatchesHistory({ me, refreshKey }: { me: MeResponse | null; refreshKey: number }) {
  const [batches, setBatches] = useState<ImportBatch[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<ImportBatch[]>('/imports/batches');
      setBatches(r);
    } catch (e) {
      setErr(humanError(e, 'No se pudo cargar el historial.'));
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const who = (id: string | null) => (id && me?.userId && id === me.userId ? 'Tú' : id ? `${id.slice(0, 8)}…` : '—');
  const fmtDate = (iso: string) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="space-y-2">
      <h3 className="font-serif text-lg tracking-tight text-slate-900">Historial de importaciones</h3>
      {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
      {batches == null ? (
        <p className="text-xs text-slate-500">Cargando…</p>
      ) : batches.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-xs text-slate-400">Aún no hay importaciones registradas.</p>
      ) : (
        <div className="overflow-auto rounded-2xl border border-slate-200/80 bg-white card-soft">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Entidad</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Archivo</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2 text-right">Nuevos</th>
                <th className="px-3 py-2 text-right">Actualiz.</th>
                <th className="px-3 py-2 text-right">Omitidos</th>
                <th className="px-3 py-2 text-right">Errores</th>
                <th className="px-3 py-2">Quién</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {batches.map((b) => (
                <tr key={b.id}>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-600">{fmtDate(b.createdAt)}</td>
                  <td className="px-3 py-2 text-slate-700">{b.entityType === 'products' ? 'Productos' : 'Ventas'}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${b.status === 'COMMITTED' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {b.status === 'COMMITTED' ? 'Confirmada' : 'Solo prueba'}
                    </span>
                  </td>
                  <td className="max-w-[14rem] truncate px-3 py-2 text-slate-500" title={b.filename ?? ''}>{b.filename ?? '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{b.rowsTotal}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{b.rowsInserted}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-sky-700">{b.rowsUpdated}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{b.rowsSkipped}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-700">{b.rowsError}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-500">{who(b.createdByUserId)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ImportDataView() {
  const { user: me } = useAuth();
  // feat/branch-write-require-active — destino de escritura para el commit de PRODUCTOS
  // (stock inicial por sucursal). El import de VENTAS no lo usa.
  const { writeBranchId, needsChoice } = useActiveWriteBranch();
  const [tab, setTab] = useState<Entity>('products');
  const [refreshKey, setRefreshKey] = useState(0);
  const [downloadingTpl, setDownloadingTpl] = useState(false);
  const [tplErr, setTplErr] = useState<string | null>(null);

  const enabledModules = me?.tenant?.enabledModules?.length ? me.tenant.enabledModules : ['crm', 'boutique', 'gastro'];
  const hasCommerce = COMMERCE_MODULES.some((m) => enabledModules.includes(m));
  const canImport = hasCommerce && can(me, 'product_catalog:write');
  // sourceModule de las ventas: primer módulo de comercio habilitado (default gastro).
  const salesSourceModule = COMMERCE_MODULES.find((m) => enabledModules.includes(m)) ?? 'gastro';

  const downloadTemplate = async () => {
    setDownloadingTpl(true); setTplErr(null);
    try {
      await apiDownload('/imports/template', 'plantilla-carga-fluidsol.xlsx');
    } catch (e) {
      setTplErr(humanError(e, 'No se pudo descargar la plantilla.'));
    } finally { setDownloadingTpl(false); }
  };

  // Guard de render (defensa en profundidad; el sidebar ya oculta y el API 403).
  if (!canImport) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white p-8 text-center card-soft">
        <p className="text-sm text-slate-500">No tienes acceso a la importación de datos.</p>
      </div>
    );
  }

  const tabCls = (t: Entity) =>
    `flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
      tab === t ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
    }`;

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div>
        <h2 className="font-serif text-2xl tracking-tight text-slate-900">Importar datos</h2>
        <p className="text-sm text-slate-500">Carga masiva de tu catálogo y ventas desde Excel. Siempre ves una vista previa antes de guardar.</p>
      </div>

      {/* Paso 0 — plantilla */}
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-slate-50/70 px-4 py-4 sm:flex-row sm:items-center sm:justify-between card-soft">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
          <div>
            <p className="text-sm font-medium text-slate-800">Descarga la plantilla</p>
            <p className="text-xs text-slate-500">Trae las hojas <span className="font-medium">Instrucciones</span>, <span className="font-medium">Productos</span> y <span className="font-medium">Ventas</span> con los encabezados correctos y un ejemplo.</p>
          </div>
        </div>
        <button className={btnGhost} onClick={downloadTemplate} disabled={downloadingTpl}>
          <span className="inline-flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> {downloadingTpl ? 'Descargando…' : 'Descargar plantilla (.xlsx)'}</span>
        </button>
      </div>
      {tplErr && <p className="alert-over rounded-xl px-3 py-2 text-xs">{tplErr}</p>}

      {/* Selector de entidad — productos ANTES que ventas (las ventas referencian productos) */}
      <div className="flex gap-2">
        <button className={tabCls('products')} onClick={() => setTab('products')}>
          <FileSpreadsheet className="h-4 w-4" /> Productos
        </button>
        <button className={tabCls('sales')} onClick={() => setTab('sales')}>
          <ShoppingCart className="h-4 w-4" /> Ventas
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200/80 bg-white p-5 card-soft">
        {tab === 'products'
          ? <ImportPanel entity="products" writeBranchId={writeBranchId} needsChoice={needsChoice} onCommitted={() => setRefreshKey((k) => k + 1)} />
          : <ImportPanel entity="sales" sourceModule={salesSourceModule} onCommitted={() => setRefreshKey((k) => k + 1)} />}
      </div>

      {/* Orden de dependencia — nota guía */}
      <p className="flex items-start gap-2 text-[11px] text-slate-400">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Importa primero tus <span className="font-medium">productos</span>: el historial de ventas se vincula a ellos por SKU o nombre. Las ventas cuyo producto no exista se marcan en la vista previa para que las revises antes de confirmar.
      </p>

      <BatchesHistory me={me} refreshKey={refreshKey} />
    </div>
  );
}
